<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('station.createStationsSubtitle') }}</span>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--primary" @click="drawer = true">
          <v-icon small>mdi-plus</v-icon>
          {{ $t('station.addStation') }}
        </button>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          {{ $t('station.stationsList') }}
          <span class="ov-count">{{ stationsList.length }}</span>
        </div>

        <div class="ov-filters">
          <!-- Sélecteur compagnie (admin uniquement) -->
          <select
            v-if="isAdminSocieteMere"
            v-model="selectedCompagnieForFilter"
            class="ov-select"
            @change="fetchStationsByCompagnie"
          >
            <option value="" disabled>{{ $t('station.selectCompany') }}</option>
            <option v-for="c in compagnieTransportList" :key="c.raisonSociale" :value="c.raisonSociale">
              {{ c.raisonSociale }}
            </option>
          </select>

          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('station.search')" />
          </div>

          <div class="ov-toggle">
            <button
              type="button"
              class="ov-toggle__opt"
              :class="{ 'ov-toggle__opt--active': viewMode === 'table' }"
              title="Tableau"
              @click="viewMode = 'table'"
            >
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </button>
            <button
              type="button"
              class="ov-toggle__opt"
              :class="{ 'ov-toggle__opt--active': viewMode === 'grid' }"
              title="Cartes"
              @click="viewMode = 'grid'"
            >
              <v-icon small>mdi-view-grid-outline</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- VUE TABLEAU -->
      <v-data-table
        v-if="viewMode === 'table'"
        :headers="tableHeaders"
        :items="stationsList"
        :search="search"
        :loading="loading"
        loader-height="2"
        class="ov-table"
        :no-data-text="$t('station.noStations')"
        :loading-text="$t('station.loadingData')"
      >
        <template #[`item.designation`]="{ item }">
          <span class="gc-designation">{{ item.designation }}</span>
        </template>
        <template #[`item.telephone1`]="{ item }">
          +225 {{ item.telephone1 }}
        </template>
        <template #[`item.telephone2`]="{ item }">
          <span v-if="item.telephone2">+225 {{ item.telephone2 }}</span>
          <span v-else class="gc-dash">—</span>
        </template>
      </v-data-table>

      <!-- VUE GRILLE -->
      <div v-else class="ov-card__body">
        <div v-if="filteredStations.length === 0" class="ov-empty">
          {{ $t('station.noStations') }}
        </div>
        <div v-else class="gc-grid">
          <CardGareTransportView
            v-for="(gare, i) in filteredStations"
            :key="i"
            :gare="gare"
            :show-company="isAdminSocieteMere"
          />
        </div>
      </div>
    </div>

    <!-- ===== PANNEAU DE CRÉATION ===== -->
    <v-navigation-drawer v-model="drawer" right fixed temporary width="420" class="ov-panel">
      <div class="ov-panel__head">
        <div>
          <div class="ov-card__kicker">{{ $t('station.addStation') }}</div>
          <div class="ov-card__title">{{ $t('station.createYourStation') }}</div>
        </div>
        <button type="button" class="ov-icon-btn" title="Fermer" @click="drawer = false">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div class="ov-panel__body">
        <div class="ov-note ov-note--info">
          <v-icon small>mdi-information-outline</v-icon>
          <span>{{ $t('station.requiredFields') }}</span>
        </div>

        <!-- Compagnie (admin société mère) -->
        <div v-if="isAdminSocieteMere" class="ov-field">
          <label class="ov-label">{{ $t('station.transportCompany') }}<span class="ov-required">*</span></label>
          <select v-model="compagnieTransportRaisonSociale" class="ov-select">
            <option :value="null" disabled>{{ $t('station.selectCompany') }}</option>
            <option v-for="c in compagnieTransportList" :key="c.raisonSociale" :value="c.raisonSociale">
              {{ c.raisonSociale }}
            </option>
          </select>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('station.stationReference') }}<span class="ov-required">*</span></label>
          <input
            v-model.trim="$v.gareTransportModel.designation.$model"
            type="text"
            class="ov-input"
            :class="{ 'is-invalid': $v.gareTransportModel.designation.$error }"
            :placeholder="$t('station.stationPlaceholder')"
          />
          <div v-if="$v.gareTransportModel.designation.$error" class="ov-error">
            {{ $t('station.requiredFieldsError') }}
          </div>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('station.email') }}<span class="ov-required">*</span></label>
          <input
            v-model.trim="$v.gareTransportModel.email.$model"
            type="email"
            class="ov-input"
            :class="{ 'is-invalid': $v.gareTransportModel.email.$error }"
            :placeholder="$t('station.emailPlaceholder')"
          />
          <div v-if="$v.gareTransportModel.email.$error" class="ov-error">
            <span v-if="!$v.gareTransportModel.email.required">{{ $t('cashRegister.newStationDialog.errors.emailRequired') }}</span>
            <span v-else-if="!$v.gareTransportModel.email.email">{{ $t('cashRegister.newStationDialog.errors.emailInvalid') }}</span>
          </div>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('station.phone1') }}<span class="ov-required">*</span></label>
          <div class="ov-input-group">
            <span class="ov-input-prefix">+225</span>
            <input
              v-model.trim="$v.gareTransportModel.telephone1.$model"
              type="tel"
              class="ov-input"
              :class="{ 'is-invalid': $v.gareTransportModel.telephone1.$error }"
              :placeholder="$t('station.phonePlaceholder')"
            />
          </div>
          <div v-if="$v.gareTransportModel.telephone1.$error" class="ov-error">
            {{ $t('cashRegister.newStationDialog.errors.phone1Required') }}
          </div>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('station.phone2') }}</label>
          <div class="ov-input-group">
            <span class="ov-input-prefix">+225</span>
            <input
              v-model="gareTransportModel.telephone2"
              type="tel"
              class="ov-input"
              :placeholder="$t('station.phonePlaceholder')"
            />
          </div>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('station.address') }}<span class="ov-required">*</span></label>
          <input
            v-model.trim="$v.gareTransportModel.adresseLocalisation.$model"
            type="text"
            class="ov-input"
            :class="{ 'is-invalid': $v.gareTransportModel.adresseLocalisation.$error }"
            :placeholder="$t('station.addressPlaceholder')"
          />
          <div v-if="$v.gareTransportModel.adresseLocalisation.$error" class="ov-error">
            {{ $t('cashRegister.newStationDialog.errors.addressRequired') }}
          </div>
        </div>
      </div>

      <template #append>
        <div class="ov-panel__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="reininitializeFormNewStation">
            <v-icon small>mdi-restore</v-icon>
            {{ $t('cashRegister.reset') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" @click="addNewStation">
            <v-icon small>mdi-content-save-outline</v-icon>
            {{ $t('station.save') }}
          </button>
        </div>
      </template>
    </v-navigation-drawer>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import { required, email } from "vuelidate/lib/validators";
import axios from "axios";
import {
  API_CREER_GARE_COMPAGNIE_TRANSPORT,
  API_RECUPERER_LISTE_GARES_PAR_COMPAGNIE,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  HEADERS,
} from "../../components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../messages/messageProcess";
import CardGareTransportView from "./widgets/CardGareTransportView.vue";

export default {
  name: "CreerGareCompagnieTransport",
  components: { CardGareTransportView },

  data() {
    return {
      errorMsg: null,
      successMsg: null,
      overlay: false,
      loading: false,
      drawer: false,
      search: "",

      stationsList: [],
      viewMode: 'table',
      compagnieTransportList: [],
      compagnieTransportRaisonSociale: null,
      selectedCompagnieForFilter: "",
      options: { index: 0, size: 100 },

      stationsRequestObject: {
        data: { compagnieTransportRaisonSociale: null },
      },

      gareTransportModel: {
        designation: null,
        email: null,
        telephone1: null,
        telephone2: null,
        adresseLocalisation: null,
        compagnieTransportRaisonSociale: null,
      },
    };
  },

  validations: {
    gareTransportModel: {
      designation: { required },
      email: { required, email },
      telephone1: { required },
      adresseLocalisation: { required },
    },
  },

  methods: {
    // Récupérer la liste des gares de la compagnie
    async fetchStations() {
      if (this.$store.state.userAuthentified.roleCode !== "RoleAdminSocieteMere") {
        this.stationsRequestObject.data.compagnieTransportRaisonSociale =
          this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        this.loading = true;
        await axios
          .post(API_RECUPERER_LISTE_GARES_PAR_COMPAGNIE, this.stationsRequestObject, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          })
          .then((response) => {
            if (response.data.status.code == 800) {
              this.stationsList = response.data.items || [];
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },

    async fetchStationsByCompagnie() {
      if (!this.selectedCompagnieForFilter) return;
      this.stationsRequestObject.data.compagnieTransportRaisonSociale = this.selectedCompagnieForFilter;
      this.loading = true;
      await axios
        .post(API_RECUPERER_LISTE_GARES_PAR_COMPAGNIE, this.stationsRequestObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.stationsList = response.data.items || [];
          } else {
            this.stationsList = [];
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.stationsList = [];
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Obtenir la liste des compagnies (admin société mère)
    async getCompagnieTransportList() {
      if (this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere") {
        await axios
          .post(API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES, this.options, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          })
          .then((response) => {
            if (response.data.status.code == 800) {
              this.compagnieTransportList = response.data.items;
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          });
      }
    },

    // Réinitialiser le formulaire
    reininitializeFormNewStation() {
      this.$v.$reset();
      this.gareTransportModel.designation = null;
      this.gareTransportModel.telephone1 = null;
      this.gareTransportModel.telephone2 = null;
      this.gareTransportModel.email = null;
      this.gareTransportModel.adresseLocalisation = null;
      this.compagnieTransportRaisonSociale = null;
    },

    // Valider et enregistrer la nouvelle gare directement en API
    addNewStation() {
      this.$v.$touch();
      if (this.$v.gareTransportModel.$invalid) {
        this.errorMsg = this.$t('station.requiredFieldsError');
        showErrorMessage();
        return;
      }
      if (
        this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere" &&
        !this.compagnieTransportRaisonSociale
      ) {
        this.errorMsg = this.$t('cashRegister.validationErrors.selectStation');
        showErrorMessage();
        return;
      }

      const stationToSave = {
        datas: [{
          designation: this.gareTransportModel.designation,
          email: this.gareTransportModel.email,
          telephone1: this.gareTransportModel.telephone1,
          telephone2: this.gareTransportModel.telephone2 || null,
          adresseLocalisation: this.gareTransportModel.adresseLocalisation,
          compagnieTransportRaisonSociale:
            this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere"
              ? this.compagnieTransportRaisonSociale
              : this.$store.state.userAuthentified.compagnieTransportRaisonSociale,
        }],
      };

      this.saveStation(stationToSave);
    },

    // Enregistrement API
    async saveStation(payload) {
      this.overlay = true;
      await axios
        .post(API_CREER_GARE_COMPAGNIE_TRANSPORT, payload, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.drawer = false;
            this.reininitializeFormNewStation();
            this.fetchStations();
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
        });
    },
  },

  computed: {
    isAdminSocieteMere() {
      return this.$store.state.userAuthentified.roleCode === 'RoleAdminSocieteMere';
    },

    filteredStations() {
      const term = (this.search || '').toLowerCase();
      if (!term) return this.stationsList;
      return this.stationsList.filter((g) => (g.designation || '').toLowerCase().includes(term));
    },

    tableHeaders() {
      const headers = [
        { text: this.$t('station.reference'), value: 'designation' },
        { text: this.$t('station.email'), value: 'email' },
        { text: this.$t('station.phone1'), value: 'telephone1' },
        { text: this.$t('station.phone2'), value: 'telephone2' },
        { text: this.$t('station.address'), value: 'adresseLocalisation' },
      ];
      if (this.$store.state.userAuthentified.roleCode === 'RoleAdminSocieteMere') {
        headers.push({ text: this.$t('station.transportCompany'), value: 'compagnieTransportRaisonSociale' });
      }
      return headers;
    },

    designationGareErrors() {
      const errors = [];
      if (!this.$v.gareTransportModel.designation.$dirty) return errors;
      !this.$v.gareTransportModel.designation.required &&
        errors.push("La désignation est obligatoire.");
      return errors;
    },

    emailGareErrors() {
      const errors = [];
      if (!this.$v.gareTransportModel.email.$dirty) return errors;
      !this.$v.gareTransportModel.email.required &&
        errors.push("Le champ E-mail est obligatoire");
      !this.$v.gareTransportModel.email.email &&
        errors.push("Entrer une adresse mail valide.");
      return errors;
    },

    premierTelephoneGareErrors() {
      const errors = [];
      if (!this.$v.gareTransportModel.telephone1.$dirty) return errors;
      !this.$v.gareTransportModel.telephone1.required &&
        errors.push("Entrer un numéro de téléphone.");
      return errors;
    },

    addresseLocalisationGareError() {
      const errors = [];
      if (!this.$v.gareTransportModel.adresseLocalisation.$dirty) return errors;
      !this.$v.gareTransportModel.adresseLocalisation.required &&
        errors.push("L'adresse est obligatoire.");
      return errors;
    },
  },

  mounted() {
    this.getCompagnieTransportList();
    this.fetchStations();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.gc-designation {
  font-weight: 700;
  color: $ov-navy;
}

.gc-dash {
  color: $ov-subtle;
}

.gc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
</style>
